import {
  TagCategory,
  useCreateTagCategoryMutation,
  useDeleteTagCategoryMutation,
  useUpdateTagCategoryMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseTagCategoryMutationProps = {}

export type UseTagCategoryMutationResult = {
  createTagCategory: (tagCategory: Partial<TagCategory>, callback: EntityMutationCallback<TagCategory> | undefined) => void
  updateTagCategory: (tagCategory: Partial<TagCategory>, callback: EntityMutationCallback<TagCategory> | undefined) => void
  deleteTagCategory: (tagCategoryId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useTagCategoryMutation = (props: UseTagCategoryMutationProps = {}): UseTagCategoryMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createTagCategory, {isLoading: isCreating}] = useCreateTagCategoryMutation()
  const [_updateTagCategory, {isLoading: isUpdating}] = useUpdateTagCategoryMutation()
  const [_deleteTagCategory, {isLoading: isDeleting}] = useDeleteTagCategoryMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useTagCategoryUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.tagcategory", "Tag Category"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagCategoryUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useTagCategoryUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.tagcategory", "Tag Category"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagCategoryUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTagCategoryUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.tagcategory", "Tag Category"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useTagCategoryUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<TagCategory>, callback: EntityMutationCallback<TagCategory> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagcategory", "Tag Category") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<TagCategory>, callback: EntityMutationCallback<TagCategory> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagcategory", "Tag Category") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tagcategory", "Tag Category") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((tagCategoryId: string, callback: EntityMutationCallback<TagCategory> | undefined) => {
    const promise = _deleteTagCategory({id: tagCategoryId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteTagCategory, handleDeleteResult])

  const createTagCategory = useCallback((tagCategory: Partial<TagCategory>, callback: EntityMutationCallback<TagCategory> | undefined) => {
    const promise = _createTagCategory(tagCategory).unwrap()
    handleCreateResult(promise, callback)
  }, [_createTagCategory, handleCreateResult])

  const updateTagCategory = useCallback((tagCategory: Partial<TagCategory>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateTagCategory(tagCategory).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateTagCategory, handleUpdateResult])

  const deleteTagCategory = useCallback((tagCategoryId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.tagcategory", "Tag Category"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.tagcategory.confirm", "Are you sure you want to delete this tag category?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(tagCategoryId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createTagCategory: createTagCategory,
    updateTagCategory: updateTagCategory,
    deleteTagCategory: deleteTagCategory,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
